import { useCallback, useState, useRef, useLayoutEffect } from "react";
import { RichText } from "../components/RichText";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteConfig, resolveImageUrl } from "../config/SiteConfigContext";
import { useCmsText } from "../hooks/useCmsText";
import { useIsMobile } from "../hooks/useIsMobile";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { Parallax } from "react-scroll-parallax";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useRequestComposer } from "../richiesta/useRequestComposer";
import { RequestComposer } from "../richiesta/RequestComposer";
import { contextFromNavState } from "../richiesta/context";

gsap.registerPlugin(ScrollTrigger);

const FALLBACK_CONTACT = "/images/fallback/contatti-testata.jpg";

export function Contact() {
  const { locale } = useParams();
  const navState = useLocation().state;
  const { t } = useTranslation();
  const cfg = useSiteConfig();
  const ca = useCmsText("about");
  const ct = useCmsText("contact");
  const isMobile = useIsMobile();
  const wide = useMediaQuery("(min-width: 1180px)");
  const containerRef = useRef<HTMLDivElement>(null);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // La richiesta cambia altezza a ogni passo: le rivelazioni allo scroll della
  // fascia contatti e delle FAQ vanno ricalcolate, o resterebbero invisibili.
  // refresh(true): «sicuro», aspetta la fine di uno scorrimento in corso invece
  // di interromperlo (portare in vista il titolo del passo è uno scorrimento).
  const refreshReveals = useCallback(() => {
    requestAnimationFrame(() => ScrollTrigger.refresh(true));
  }, []);

  // «Inviaci una richiesta»: stessi dati e stesso invio di sempre (source
  // contact_page, lingua dall'URL), ma guidata. Il motore vive qui, la vista
  // è condivisa con il widget.
  const request = useRequestComposer({
    source: "contact_page",
    locale: locale || "it",
    gaLocation: "contact_page",
    surface: "page",
    onLayoutChange: refreshReveals,
    // Dalla CTA di Collezioni «Vuoi combinare più confezioni regalo?»: si apre sull'offerta.
    initialContext: contextFromNavState(navState),
  });

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const ctx = gsap.context(() => {
      // Con il movimento ridotto le sezioni compaiono subito, senza scivolare.
      gsap.matchMedia().add("(prefers-reduced-motion: no-preference)", () => {
        gsap.utils.toArray<HTMLElement>(".gs-ct-reveal").forEach((el) => {
          gsap.from(el, { y: 40, opacity: 0, duration: 1, ease: "expo.out", scrollTrigger: { trigger: el, start: "top 85%", once: true } });
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const faqs = [
    { q: ct("faq1Q"), a: ct("faq1A") },
    { q: ct("faq2Q"), a: ct("faq2A") },
    { q: ct("faq3Q"), a: ct("faq3A") },
    { q: ct("faq4Q"), a: ct("faq4A") },
  ];

  return (
    <div ref={containerRef}>
      {/* ═══ HERO ═══ */}
      <section style={{ position: "relative", height: isMobile ? 280 : 400, overflow: "hidden" }}>
        <Parallax speed={isMobile ? -3 : -10} style={{ position: "absolute", inset: "-20% 0", height: "140%" }}>
          <div style={{ width: "100%", height: "100%", backgroundImage: `url(${ca.image("contactImage") ? resolveImageUrl(ca.image("contactImage")!) : FALLBACK_CONTACT})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        </Parallax>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%)" }} />
        <div style={{ position: "absolute", bottom: isMobile ? 32 : 56, left: isMobile ? 24 : 48, zIndex: 2 }}>
          <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 3, textTransform: "uppercase", color: "#fff", margin: "0 0 10px" }}>
            {ct("overline")}
          </p>
          <h1 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 32 : 48, fontWeight: 400, color: "#fff", margin: 0, lineHeight: 1.1, letterSpacing: -0.5 }}>
            {ct("title")}
          </h1>
        </div>
      </section>

      {/* ═══ RICHIESTA GUIDATA ═══ */}
      <section style={{ background: cfg.color_body_bg, padding: isMobile ? "40px 16px 64px" : wide ? "80px 24px 120px" : "64px 24px 96px" }}>
        <div
          className="gs-ct-reveal"
          style={{
            maxWidth: wide ? 1120 : 760,
            margin: "0 auto",
            background: cfg.color_light_bg,
            borderRadius: 20,
            padding: isMobile ? "28px 20px" : wide ? "56px" : "40px",
            boxShadow: "0 4px 40px rgba(28,75,59,0.07)",
          }}
        >
          <RequestComposer api={request} variant="page" />
        </div>
      </section>

      {/* ═══ INFO STRIP ═══ */}
      <section className="gs-ct-reveal" style={{ background: cfg.color_dark, padding: isMobile ? "48px 24px" : "64px 48px" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: isMobile ? "flex" : "grid", flexDirection: "column", gridTemplateColumns: "1fr 1fr 1fr", gap: isMobile ? 32 : 48 }}>
          {[
            { icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", label: t("contact.email"), value: cfg.contact_email || "info@granborgo.com", href: `mailto:${cfg.contact_email || "info@granborgo.com"}` },
            { icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z", label: ca("contactPhone") || "Telefono", value: cfg.contact_phone || "+4967629499360", href: `tel:${(cfg.contact_phone || "").replace(/\s/g, "")}` },
            { icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z", label: ca("contactAddress") || "Indirizzo", value: cfg.contact_address || "Granborgo GmbH\nAn der Molkerei 8\n56288 Kastellaun, Deutschland", href: undefined },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5"><path d={item.icon} /></svg>
              </div>
              <p style={{ fontSize: 10, fontWeight: 600, letterSpacing: 2.5, textTransform: "uppercase", color: "#fff", margin: 0 }}>{item.label}</p>
              {item.href ? (
                <a href={item.href} style={{ fontSize: 15, fontFamily: cfg.font_heading, color: "#fff", textDecoration: "none", lineHeight: 1.5, transition: "opacity .2s", whiteSpace: "pre-line" }}
                  onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.65"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
                >{item.value}</a>
              ) : (
                <p style={{ fontSize: 15, fontFamily: cfg.font_heading, color: "#fff", margin: 0, lineHeight: 1.5, whiteSpace: "pre-line" }}>{item.value}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section style={{ background: cfg.color_light_bg, padding: isMobile ? "56px 24px" : "88px 48px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div className="gs-ct-reveal" style={{ marginBottom: isMobile ? 32 : 48 }}>
            <div style={{ width: 40, height: 1, background: cfg.color_muted, marginBottom: 20 }} />
            <h2 style={{ fontFamily: cfg.font_heading, fontSize: isMobile ? 24 : 32, fontWeight: 400, color: cfg.color_dark, margin: 0, letterSpacing: -0.3 }}>
              {ct("faqTitle")}
            </h2>
          </div>
          <div className="gs-ct-reveal" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ borderBottom: "1px solid rgba(28,75,59,0.89)" }}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 0", background: "none", border: "none", cursor: "pointer", fontSize: 15, fontWeight: 500, color: cfg.color_dark, textAlign: "left" }}
                >
                  {faq.q}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cfg.color_dark} strokeWidth="2"
                    style={{ flexShrink: 0, transition: "transform .3s", transform: openFaq === i ? "rotate(180deg)" : "rotate(0)" }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
                <div style={{ maxHeight: openFaq === i ? 200 : 0, overflow: "hidden", transition: "max-height .3s ease" }}>
                  <RichText html={faq.a} style={{ fontSize: 14, color: "rgba(28,75,59,0.86)", lineHeight: 1.8, margin: "0 0 20px", paddingRight: 32 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
